/**
 * Make the pictures in an email body actually appear.
 *
 * ## Two separate reasons they were broken
 *
 * Measured on the dev tenant, over 300 message bodies containing `<img>`:
 *
 *   * **252 referenced a remote `http(s)` image.** `server.py` sets
 *     `img-src 'self' data: blob: <s3>`, deliberately: the comment there (S12)
 *     records that `img-src ... https:` allows a GET to any host and a URL
 *     carries data in its query string, which is the exfiltration channel
 *     `connect-src` is tightened to close. Every one of those images was
 *     blocked by the browser.
 *   * **50 referenced a `cid:` inline attachment.** Nothing in the repo
 *     resolves those, and nothing stores inline attachments to resolve them
 *     AGAINST — `gmail_sync` persists no attachment metadata at all. Those
 *     stay broken until the ingest work lands; this module leaves them
 *     untouched rather than pretending.
 *
 * ## Why blob URLs and not a token in the `src`
 *
 * The body renders in a sandboxed iframe via `srcDoc`, which cannot send an
 * `Authorization` header — so a direct `src="/api/mail/image?u=..."` would
 * have to authenticate some other way. Putting a token in the query string is
 * the defect class #603/#637 already fixed once (secrets in URLs end up in
 * logs, referrers and history), and relying on a session cookie would couple
 * image rendering to an auth mode this app does not primarily use.
 *
 * So the PARENT fetches each image through the authenticated proxy, wraps the
 * bytes in an object URL, and rewrites the body to point at that. `blob:` is
 * already permitted by the CSP, no credential ever reaches the iframe, and the
 * sender learns nothing about the reader — the fetch happens server-side.
 *
 * Object URLs are revoked on unmount. Without that, opening twenty messages
 * pins twenty images in memory for the life of the tab.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

import { API } from '../../lib/api';

function imageSources(html) {
    if (!html) return [];
    const out = [];
    const re = /<img\b[^>]*?\ssrc\s*=\s*["']([^"']+)["']/gi;
    let match = re.exec(html);
    while (match) {
        out.push(match[1]);
        match = re.exec(html);
    }
    return out;
}

/** Every distinct remote image URL in a body. */
export function remoteImageUrls(html) {
    return [...new Set(imageSources(html).filter((u) => /^https?:\/\//i.test(u)))];
}

/**
 * Every distinct `cid:` reference in a body, with the prefix stripped.
 *
 * These point at an INLINE ATTACHMENT of the same message. Until the ingest
 * recorded attachment metadata there was nothing to resolve them against, so
 * they were left alone; now each one can be matched to a part by Content-ID
 * and fetched from the download route.
 */
export function inlineImageIds(html) {
    return [...new Set(
        imageSources(html)
            .filter((u) => /^cid:/i.test(u))
            .map((u) => u.slice(4).trim())
            .filter(Boolean),
    )];
}

/** The recorded attachment a `cid:` reference points at, or undefined. */
export function attachmentForCid(attachments, cid) {
    const target = (cid || '').replace(/^<|>$/g, '').toLowerCase();
    if (!target) return undefined;
    return (attachments || []).find(
        (a) => (a.content_id || '').toLowerCase() === target,
    );
}

/** Swap every occurrence of each original URL for its local stand-in. */
export function rewriteImageUrls(html, mapping) {
    let out = html;
    for (const [original, replacement] of Object.entries(mapping)) {
        // Split/join rather than a RegExp: an email URL is full of regex
        // metacharacters (`?`, `+`, `.`, parentheses in tracking params) and
        // escaping them by hand is how one malformed URL breaks every image
        // in the body.
        out = out.split(original).join(replacement);
    }
    return out;
}

/**
 * `html` with its remote images fetched through the proxy and swapped for
 * object URLs. Returns the ORIGINAL html until they resolve, so the text of
 * the mail is readable immediately rather than after the last image.
 */
export function useProxiedEmailImages(html, getAuthHeader, message = {}) {
    const { commId, attachments } = message;
    const urls = useMemo(() => remoteImageUrls(html), [html]);
    // A `cid:` only resolves when the ingest recorded a part with that
    // Content-ID. One that does not match is left untouched rather than
    // rewritten to a URL that 404s — the alt text is more use than a second
    // broken box.
    const cids = useMemo(() => {
        if (!commId) return [];
        return inlineImageIds(html)
            .map((cid) => [cid, attachmentForCid(attachments, cid)])
            .filter(([, part]) => Boolean(part));
    }, [html, commId, attachments]);
    const [mapping, setMapping] = useState({});
    const objectUrlsRef = useRef([]);

    // A stable key, so re-rendering with an equal-but-new array does not
    // re-fetch every image whenever something else on the page changes.
    const key = [urls.join('|'), cids.map(([c]) => c).join('|'), commId || ''].join('::');

    useEffect(() => {
        if (!urls.length && !cids.length) {
            setMapping({});
            return undefined;
        }
        let cancelled = false;
        const created = [];

        const asObjectUrl = async (requestUrl, config) => {
            const res = await axios.get(requestUrl, {
                responseType: 'blob',
                headers: getAuthHeader?.() || {},
                ...config,
            });
            const objectUrl = URL.createObjectURL(res.data);
            created.push(objectUrl);
            return objectUrl;
        };

        (async () => {
            const resolved = {};
            await Promise.all([
                ...cids.map(async ([cid, part]) => {
                    try {
                        resolved[`cid:${cid}`] = await asObjectUrl(
                            `${API}/mail/messages/${encodeURIComponent(commId)}`
                            + `/attachments/${encodeURIComponent(part.attachment_id)}`,
                        );
                    } catch {
                        // Same rule as a refused remote image: leave the
                        // original `src` so the alt text shows.
                    }
                }),
                ...urls.map(async (url) => {
                    try {
                        resolved[url] = await asObjectUrl(`${API}/mail/image`, {
                            params: { u: url },
                        });
                    } catch {
                        // Leave the original `src` in place. It will not
                        // render — the CSP still blocks it — but the browser
                        // shows the `alt` text, which beats a silently empty
                        // box. A refused image is a normal outcome here: the
                        // proxy rejects private hosts, non-images and
                        // anything over 5 MB.
                    }
                }),
            ]);
            if (cancelled) {
                created.forEach(URL.revokeObjectURL);
                return;
            }
            objectUrlsRef.current = created;
            setMapping(resolved);
        })();

        return () => {
            cancelled = true;
            objectUrlsRef.current.forEach(URL.revokeObjectURL);
            objectUrlsRef.current = [];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, getAuthHeader]);

    return useMemo(
        () => (Object.keys(mapping).length ? rewriteImageUrls(html, mapping) : html),
        [html, mapping],
    );
}
